/**
 * useMobileAutoScroll
 *
 * Drop-in hook for any horizontal carousel using CSS scroll-snap. Auto-
 * advances one snap-card every `intervalMs` on mobile only. After the
 * last real card, lands on a (caller-rendered) cloned first card and
 * instant-jumps back to the real first card — so the loop feels like
 * forward continuation rather than a visible reverse-swipe rewind.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useMobileAutoScroll(ref, { itemCount: frames.length });
 *   return (
 *     <div ref={ref} className="md:hidden flex overflow-x-auto snap-x">
 *       {frames.map(f => <Card key={f.id} {...f} />)}
 *       <Card key="clone" {...frames[0]} aria-hidden="true" />
 *     </div>
 *   );
 *
 * The caller must render one extra clone of the first card at the end
 * of the children list (with aria-hidden="true") so the auto-scroll
 * can advance forward into it before instant-jumping back to the real
 * first card for a seamless loop.
 *
 * Behaviour:
 *   - Only arms when `(min-width: 768px)` does NOT match (mobile-ish).
 *   - Respects `prefers-reduced-motion`.
 *   - 4 000 ms per slide by default.
 *   - Targets each child's `offsetLeft` directly so `snap-center`
 *     padding-tricks don't trip the loop-back detection (the previous
 *     `scrollWidth - clientWidth` math was off by one snap-step and
 *     would skip the final card entirely on 3-card mobile carousels).
 *   - Pauses for 6 s after any touch / pointer interaction so the user
 *     can swipe without the timer immediately yanking the scroll back.
 *   - Pauses while the page is hidden (visibilitychange).
 */
import { useEffect, useRef } from "react";

const DEFAULT_INTERVAL_MS = 4000;
const POST_INTERACTION_PAUSE_MS = 6000;
const MOBILE_QUERY = "(max-width: 767px)";
// How long after triggering a smooth scroll do we wait before
// instant-jumping back to the real first card? Should be a hair longer
// than the browser's native smooth-scroll duration (~300-400 ms).
const SMOOTH_SCROLL_SETTLE_MS = 550;


export default function useMobileAutoScroll(
    containerRef,
    { itemCount = 0, intervalMs = DEFAULT_INTERVAL_MS, enabled = true } = {},
) {
    const pausedUntilRef = useRef(0);

    useEffect(() => {
        if (!enabled || itemCount <= 1) return undefined;
        if (typeof window === "undefined") return undefined;

        const mql = window.matchMedia(MOBILE_QUERY);
        const reducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        );

        let timerId = null;
        let detachInteraction = null;
        let detachVisibility = null;

        const currentIndex = () => {
            const container = containerRef.current;
            if (!container) return 0;
            const children = container.children;
            if (children.length === 0) return 0;
            // Whichever child's centre is closest to the container's
            // visible centre is the "active" card.
            const center = container.scrollLeft + container.clientWidth / 2;
            let best = 0;
            let bestDist = Infinity;
            for (let i = 0; i < children.length; i += 1) {
                const c = children[i];
                const cCenter = c.offsetLeft + c.offsetWidth / 2;
                const dist = Math.abs(cCenter - center);
                if (dist < bestDist) {
                    bestDist = dist;
                    best = i;
                }
            }
            return best;
        };

        const scrollToIndex = (index, { smooth = true } = {}) => {
            const container = containerRef.current;
            if (!container) return;
            const child = container.children[index];
            if (!child) return;
            // Centre the child within the container's visible area.
            const left = (
                child.offsetLeft
                - (container.clientWidth - child.offsetWidth) / 2
            );
            if (smooth) {
                container.scrollTo({ left, behavior: "smooth" });
            } else {
                // Temporarily disable smooth scroll on the container so
                // CSS `scroll-behavior: smooth` doesn't animate the jump.
                const prev = container.style.scrollBehavior;
                container.style.scrollBehavior = "auto";
                container.scrollLeft = left;
                // Force a reflow before re-enabling, otherwise some
                // browsers batch the change with the next smooth scroll.
                void container.offsetHeight;
                container.style.scrollBehavior = prev || "";
            }
        };

        const tick = () => {
            const container = containerRef.current;
            if (!container) return;
            if (document.hidden) return;
            if (Date.now() < pausedUntilRef.current) return;

            const childCount = container.children.length;
            if (childCount === 0) return;

            const idx = currentIndex();
            // The container is expected to have itemCount + 1 children
            // (N real + 1 clone of the first). If a caller doesn't
            // render the clone, we degrade gracefully to a plain
            // wrap-around at the last real card.
            const hasClone = childCount === itemCount + 1;
            const cloneIndex = hasClone ? childCount - 1 : -1;

            // Standard advance.
            const nextIdx = idx + 1;

            if (nextIdx < childCount) {
                scrollToIndex(nextIdx, { smooth: true });
                // If we just smoothed onto the clone, schedule an
                // instant teleport back to the real first card after
                // the smooth scroll settles. This gives the loop a
                // continuous-forward feel rather than a reverse rewind.
                if (hasClone && nextIdx === cloneIndex) {
                    window.setTimeout(() => {
                        if (Date.now() < pausedUntilRef.current) return;
                        scrollToIndex(0, { smooth: false });
                    }, SMOOTH_SCROLL_SETTLE_MS);
                }
            } else {
                // No clone, already on last card — fall back to a soft
                // rewind so we don't get stuck.
                scrollToIndex(0, { smooth: true });
            }
        };

        const arm = () => {
            if (timerId) return;
            timerId = window.setInterval(tick, intervalMs);

            const container = containerRef.current;
            if (!container) return;

            const onInteract = () => {
                pausedUntilRef.current = Date.now() + POST_INTERACTION_PAUSE_MS;
            };
            container.addEventListener("touchstart", onInteract, { passive: true });
            container.addEventListener("pointerdown", onInteract);
            container.addEventListener("wheel", onInteract, { passive: true });
            detachInteraction = () => {
                container.removeEventListener("touchstart", onInteract);
                container.removeEventListener("pointerdown", onInteract);
                container.removeEventListener("wheel", onInteract);
            };

            const onVis = () => {
                if (!document.hidden) {
                    // Re-anchor so a long hidden interval doesn't fire instantly.
                    pausedUntilRef.current = Date.now() + 500;
                }
            };
            document.addEventListener("visibilitychange", onVis);
            detachVisibility = () => {
                document.removeEventListener("visibilitychange", onVis);
            };
        };

        const disarm = () => {
            if (timerId) {
                window.clearInterval(timerId);
                timerId = null;
            }
            if (detachInteraction) {
                detachInteraction();
                detachInteraction = null;
            }
            if (detachVisibility) {
                detachVisibility();
                detachVisibility = null;
            }
        };

        const sync = () => {
            if (mql.matches && !reducedMotion.matches) {
                arm();
            } else {
                disarm();
            }
        };

        sync();
        mql.addEventListener("change", sync);
        reducedMotion.addEventListener("change", sync);

        return () => {
            mql.removeEventListener("change", sync);
            reducedMotion.removeEventListener("change", sync);
            disarm();
        };
    }, [containerRef, itemCount, intervalMs, enabled]);
}
